import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { Constants, transactionTypes } from '../../../shared/utilities/Constants';
import { selectAccountStatementsDateInput, selectCloseAccountAccountStatments, selectCloseAccountTaxForms, selectCloseAccountTradeConfirmations, selectIsFromEdit, selectIsOneTimeCloseAccount, selectIsOneTimeTotalDist, selectTaxFormsYearInput, selectTradeConfirmationsDateInput, selectTransactionType } from '../../../create-request/reducers';
import { toAccountAllowedChars } from '../../utilities/regex-patterns';
import { ValidateInputService } from '../../services/validate-input.service';
import { ItExtraPrompts, TransactionForm } from '../../../create-request/models/form/TransactionForm';
import { createControl } from '../../../create-request/utilities/formUtilities';

@Component({
  standalone: false,
  selector: 'mm-reports-statements-section',
  templateUrl: './total-distribution-reports-statements.component.html',
  styleUrls: ['./total-distribution-reports-statements.component.scss'],
})
export class TotalDistReportsStatementsComponent implements OnInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() editFormDetails: TransactionForm | undefined;
  destroy$: Subject<boolean> = new Subject<boolean>();
  taxForms = new FormControl(false)
  accountStatements = new FormControl(false)
  tradeConfirmations = new FormControl(false)
  taxFormsDateInputControl = new FormControl<string>('', [Validators.required, Validators.minLength(4), Validators.maxLength(4), this.yearValidator])
  accountStatementsDateInputControl = new FormControl<string>('', [Validators.required, this.accountsStatementsValidator.bind(this)])
  tradeConfirmationsDateInputControl = new FormControl<string>('', [Validators.required, this.tradeConfirmationsValidator.bind(this)])
  isOneTimeCloseAccount: boolean = false
  taxFormsSelected: boolean = false
  accountStatementsSelected: boolean = false
  tradeConfirmationsSelected: boolean = false
  constants = Constants;
  taxFormsAllowedChars = toAccountAllowedChars;
  isFromEdit: boolean;
  extraPromptsEdit: ItExtraPrompts;
  transactionType: string;
  constructor(
    private store: Store,
    public validationService: ValidateInputService
  ) { }

  ngOnInit(): void {
    this.store.select(selectTransactionType).pipe(takeUntil(this.destroy$)).subscribe(transactionType => { this.transactionType = transactionType});

    this.store.select(selectIsOneTimeCloseAccount).pipe(takeUntil(this.destroy$)).subscribe(isOneTimeCloseAccount => {
      this.isOneTimeCloseAccount = isOneTimeCloseAccount
      if (isOneTimeCloseAccount) {
        this.parentForm.setControl('taxForms', this.taxForms);
        this.parentForm.setControl('accountStatements', this.accountStatements);
        this.parentForm.setControl('tradeConfirmations', this.tradeConfirmations);
        this.setupCheckboxDateControl(this.taxForms, 'taxFormsDateInputControl', this.taxFormsDateInputControl);
        this.setupCheckboxDateControl(this.accountStatements, 'accountStatementsDateInputControl', this.accountStatementsDateInputControl);
        this.setupCheckboxDateControl(this.tradeConfirmations, 'tradeConfirmationsDateInputControl', this.tradeConfirmationsDateInputControl);
      }
      else {
        this.parentForm.removeControl('taxForms');
        this.parentForm.removeControl('accountStatements');
        this.parentForm.removeControl('tradeConfirmations');
        this.parentForm.removeControl('taxFormsDateInputControl');
        this.parentForm.removeControl('accountStatementsDateInputControl');
        this.parentForm.removeControl('tradeConfirmationsDateInputControl');
        this.taxForms.setValue(false)
        this.accountStatements.setValue(false)
        this.tradeConfirmations.setValue(false)
        this.taxFormsDateInputControl.setValue('');
        this.accountStatementsDateInputControl.setValue('');
        this.tradeConfirmationsDateInputControl.setValue('');
        this.taxFormsDateInputControl.markAsUntouched();
        this.accountStatementsDateInputControl.markAsUntouched();
        this.tradeConfirmationsDateInputControl.markAsUntouched();
      }
    });

    this.store.select(selectCloseAccountTaxForms).pipe(takeUntil(this.destroy$)).subscribe(taxFormsSelected => {
      this.taxFormsSelected = taxFormsSelected
    });

    this.store.select(selectCloseAccountAccountStatments).pipe(takeUntil(this.destroy$)).subscribe(accountStatementsSelected => {
      this.accountStatementsSelected = accountStatementsSelected
    });

    this.store.select(selectCloseAccountTradeConfirmations).pipe(takeUntil(this.destroy$)).subscribe(tradeConfirmationsSelected => {
      this.tradeConfirmationsSelected = tradeConfirmationsSelected
    });

    this.store.select(selectIsFromEdit).pipe(takeUntil(this.destroy$)).subscribe((isFromEdit) => {
      this.isFromEdit = isFromEdit
      if (this.isFromEdit && this.editFormDetails){
        this.extraPromptsEdit = this.editFormDetails.extraPrompts as ItExtraPrompts;
        if (this.extraPromptsEdit.taxForms){
          createControl(this.extraPromptsGroup, 'taxForms', this.extraPromptsEdit.taxForms, undefined, undefined, true);
          createControl(this.extraPromptsGroup, 'taxFormsDateInputControl', this.extraPromptsEdit.taxFormsDateInputControl ?? '', undefined, undefined, true);
          this.taxForms.setValue(this.extraPromptsEdit.taxForms);
          this.taxFormsDateInputControl.setValue(this.extraPromptsEdit.taxFormsDateInputControl ?? '');
          this.extraPromptsEdit.taxForms = false;
          this.extraPromptsEdit.taxFormsDateInputControl = '';
        }
        if (this.extraPromptsEdit.accountStatements){
          createControl(this.extraPromptsGroup, 'accountStatements', this.extraPromptsEdit.accountStatements, undefined, undefined, true);
          createControl(this.extraPromptsGroup, 'accountStatementsDateInputControl', this.extraPromptsEdit.accountStatementsDateInputControl ?? '', undefined, undefined, true);
          this.accountStatements.setValue(this.extraPromptsEdit.accountStatements);
          this.accountStatementsDateInputControl.setValue(this.extraPromptsEdit.accountStatementsDateInputControl ?? '');
          this.extraPromptsEdit.accountStatements = false;
          this.extraPromptsEdit.accountStatementsDateInputControl = '';
        }
        if(this.extraPromptsEdit.tradeConfirmations){
          createControl(this.extraPromptsGroup, 'tradeConfirmations', this.extraPromptsEdit.tradeConfirmations, undefined, undefined, true);
          createControl(this.extraPromptsGroup, 'tradeConfirmationsDateInputControl', this.extraPromptsEdit.tradeConfirmationsDateInputControl ?? '',  undefined, undefined, true);
          this.tradeConfirmations.setValue(this.extraPromptsEdit.tradeConfirmations);
          this.tradeConfirmationsDateInputControl.setValue(this.extraPromptsEdit.tradeConfirmationsDateInputControl ?? '');
          this.extraPromptsEdit.tradeConfirmations = false;
          this.extraPromptsEdit.tradeConfirmationsDateInputControl = '';
        }
      }
    })
  }

  get extraPromptsGroup(): UntypedFormGroup {
    let group = this.parentForm?.get('extraPrompts');
    if (!(group instanceof UntypedFormGroup)) {
      group = new UntypedFormGroup({});
      this.parentForm?.setControl('extraPrompts', group);
    }
    return group as UntypedFormGroup;
  }

  onCheckBoxChanged(controlName: string) {
    if (controlName === 'taxForms' && !this.taxFormsSelected) {
      this.taxFormsDateInputControl.setValue('');
      this.taxFormsDateInputControl.markAsUntouched();
    }
    if (controlName === 'accountStatements' && !this.accountStatementsSelected) {
      this.accountStatementsDateInputControl.setValue('');
      this.accountStatementsDateInputControl.markAsUntouched();
    }
    if (controlName === 'tradeConfirmations' && !this.tradeConfirmationsSelected) {
      this.tradeConfirmationsDateInputControl.setValue('')
      this.tradeConfirmationsDateInputControl.markAsUntouched();
    }
  }

  yearValidator(control: AbstractControl): ValidationErrors | null {
    if (isNaN(control.value) || Number(control.value) > new Date().getFullYear() || Number(control.value) <= 0) {
      return { invalidYear: true};
    }
    return null;
  }

  setupCheckboxDateControl(checkboxControl: FormControl, dateControlName: string, dateControl: FormControl) {
    checkboxControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked: boolean) => {
      if (checked) {
        this.parentForm.setControl(dateControlName, dateControl);
      } else {
        this.parentForm.removeControl(dateControlName);
      }
    });
  }

  validateInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let enteredValue = input.value;
    enteredValue = enteredValue.replace(/[^\d\/]/g, '');

    const digits = enteredValue.replace(/\D/g, '');

    let formattedDate = '';

    if(enteredValue.length === 3 && enteredValue[2] === '/' ){
      formattedDate = enteredValue;
    } else if (digits.length <= 2) {
      formattedDate = digits
    } else if (digits.length <= 6) {
      formattedDate = digits.slice(0, 2) + '/' + digits.slice(2);
    } else {
      formattedDate = digits.slice(0, 2) + '/' + digits.slice(2, 6);
    }

    this.accountStatementsDateInputControl.setValue(formattedDate)

    setTimeout(() => {
      const len = input.value.length;
      input.setSelectionRange(len, len);
      }, 0);
  }

  accountsStatementsValidator(control: AbstractControl): ValidationErrors | null{
    const value = control.value;

    const [yyyy, mm] = value.split('-');
    const year = parseInt(yyyy, 10);
    const month = parseInt(mm, 10);

    const now = new Date();
    const inputDate = new Date(year, month - 1);
    const current = new Date(now.getFullYear(), now.getMonth());

    if(inputDate > current) {
      return { invalidAccountsStatementsDate: true}
    }

    return null;
  }

  tradeConfirmationsValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (isNaN(Date.parse(value))) {
      return { invalidTradeConfirmationsDate: true };
    }

    const [yyyy, mm, dd] = value.split('-');
    const year = parseInt(yyyy, 10);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);

    const now = new Date();
    const inputDate = new Date(year, month - 1, day);
    const current = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if(inputDate > current) {
      return { invalidTradeConfirmationsDate: true};
    }

    return null;
  }

}